import React, { useRef, useState } from "react";
import "./hero.css";

const TERMINAL_LINES = [
 { text: "$ forge new --client your-vision", cls: "t-cmd" },
 { text: "> heating ideas .............. done", cls: "t-ok" },
 { text: "> hammering architecture ..... done", cls: "t-ok" },
 { text: "> quenching bugs ............. done", cls: "t-ok" },
 { text: "> polishing interface ........ done", cls: "t-ok" },
 { text: "✔ shipped to production", cls: "t-done" },
];

const scrollToSection = (id) => {
 const element = document.getElementById(id);
 if (!element) return;
 window.scrollTo({ top: element.getBoundingClientRect().top + window.scrollY - 90, behavior: "smooth" });
};

export function Hero() {
 const heroRef = useRef(null);
 const termRef = useRef(null);
 const [bursts, setBursts] = useState([]);
 const [forgeRun, setForgeRun] = useState(0);

 const handleMouseMove = (e) => {
  const el = heroRef.current;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
  el.style.setProperty("--my", `${e.clientY - rect.top}px`);
 };

 const handleClick = (e) => {
  const el = heroRef.current;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const id = Date.now() + Math.random();
  const burst = {
   id,
   x: e.clientX - rect.left,
   y: e.clientY - rect.top,
   sparks: Array.from({ length: 12 }).map(() => {
    const angle = Math.random() * Math.PI * 2;
    const dist = 40 + Math.random() * 80;
    return {
     dx: Math.cos(angle) * dist,
     dy: Math.sin(angle) * dist - 40,
     size: 2 + Math.random() * 3,
    };
   }),
  };
  setBursts((prev) => [...prev, burst]);
  setTimeout(() => setBursts((prev) => prev.filter((b) => b.id !== id)), 900);
 };

 const tiltMove = (e) => {
  const el = termRef.current;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const px = (e.clientX - rect.left) / rect.width - 0.5;
  const py = (e.clientY - rect.top) / rect.height - 0.5;
  el.style.transform = `perspective(900px) rotateY(${px * 8}deg) rotateX(${-py * 6}deg)`;
 };

 const tiltReset = () => {
  const el = termRef.current;
  if (!el) return;
  el.style.transform = "perspective(900px) rotateY(0deg) rotateX(0deg)";
 };

 return (
  <section className="hero" id="home" ref={heroRef} onMouseMove={handleMouseMove} onClick={handleClick}>
   <div className="hero-grid-bg" aria-hidden="true"></div>
   <div className="hero-glow" aria-hidden="true"></div>
   <div className="hero-heat" aria-hidden="true"></div>
   <div className="hero-embers" aria-hidden="true">
    {Array.from({ length: 14 }).map((_, i) => (
     <span key={i} className={`ember ember-${i}`}></span>
    ))}
   </div>

   {bursts.map((burst) => (
    <div key={burst.id} className="spark-burst" style={{ left: burst.x, top: burst.y }} aria-hidden="true">
     {burst.sparks.map((spark, i) => (
      <span
       key={i}
       className="spark"
       style={{
        "--dx": `${spark.dx}px`,
        "--dy": `${spark.dy}px`,
        width: spark.size,
        height: spark.size,
       }}
      ></span>
     ))}
    </div>
   ))}

   <div className="hero-inner">
    <div className="hero-copy">
     <div className="hero-eyebrow mono">{"// software forging company"}</div>
     <h1 className="hero-title">
      We forge <span className="gradient-text">ideas</span>
      <br />
      into software.
     </h1>
     <div className="molten-bar" aria-hidden="true"></div>
     <p className="hero-slogan">
      We forge your way into the future — web, mobile, AI &amp; ML solutions
      hammered to fit your business, tempered for scale.
     </p>
     <div className="hero-cta">
      <button className="btn-primary" onClick={() => scrollToSection("contacts")}>
       Start a project
      </button>
      <button className="btn-ghost" onClick={() => scrollToSection("services")}>
       Explore services
      </button>
     </div>
     <div className="hero-facts mono">
      <div className="hero-fact">
       <span className="hero-fact-value">10+</span> years of expertise
      </div>
      <div className="hero-fact-divider"></div>
      <div className="hero-fact">
       <span className="hero-fact-value">full-cycle</span> development
      </div>
      <div className="hero-fact-divider"></div>
      <div className="hero-fact">
       <span className="hero-fact-value">agile</span> &amp; tailored
      </div>
     </div>
    </div>

    <div className="hero-terminal-wrap">
     <div
      className="hero-terminal"
      ref={termRef}
      onMouseMove={tiltMove}
      onMouseLeave={tiltReset}
      onClick={(e) => {
       e.stopPropagation();
       setForgeRun((n) => n + 1);
      }}
      title="Click to re-forge"
     >
      <div className="terminal-bar">
       <span className="terminal-dot dot-red"></span>
       <span className="terminal-dot dot-yellow"></span>
       <span className="terminal-dot dot-green"></span>
       <span className="terminal-title mono">softforging — forge</span>
       <span className="terminal-replay mono">{"⟳ re-forge"}</span>
      </div>
      <div className="terminal-body mono" key={forgeRun}>
       {TERMINAL_LINES.map((line, idx) => (
        <div key={idx} className={`terminal-line ${line.cls}`} style={{ animationDelay: `${0.6 + idx * 0.9}s` }}>
         {line.text}
        </div>
       ))}
       <div className="terminal-cursor-line">
        <span className="terminal-prompt">$</span>
        <span className="terminal-cursor"></span>
       </div>
      </div>
     </div>
    </div>
   </div>

   <div className="forge-seam" aria-hidden="true"></div>

   <button className="hero-scroll-hint" onClick={() => scrollToSection("purpose")} aria-label="Scroll down">
    <span className="mono">scroll</span>
    <div className="scroll-line"></div>
   </button>
  </section>
 );
}
